import { useCallback, useEffect, useState } from 'react'

import { Grid } from '@mantine/core'

import { useQuery } from '@redwoodjs/web'

import PaginationArea from 'src/components/Navigation/PaginationArea/PaginationArea'
import DateRangeMenu from 'src/components/Overlays/DateRangeMenu'
import { toast } from 'src/components/Overlays/Toast/Toast'
import { GET_CALLS } from 'src/graphql/activityLogs.graphql'
import { GET_VOICE_AGENT_CALL_METRICS } from 'src/graphql/callAnaltyics.graphql'
import { VoiceAgentAnalysisResultName } from 'src/graphql/types/voiceAgentResult'
import { GET_VOICE_AGENTS } from 'src/graphql/voiceAgents.graphql'

import FilterMenu from '../../../../components/Overlays/FiltersMenu/FilterMenu'
import { FiltersPortal } from '../../components/FiltersPortal'
import AnalyticsCard from '../../../AnalyticsPage/components/AnalyticsCard'
import { CallAnalysisAlert } from '../../components/CallAnalysisAlert'
import {
  activityLogConnectedStatusOptions,
  serializeDateRangeForUrl,
  updateUrl,
  voiceAgentAnalysisResultOptions,
} from '../../utils'
import { getUrlParams } from '../../utils'

import VoiceAgentAnalysisTable from './components/VoiceAgentAnalysisTable'
import VoiceAgentBarChart from './components/VoiceAgentBarChart'
import VoiceAgentSwarmPlot from './components/VoiceAgentSwarmPlot'

const CallQualityTab = () => {
  const urlParams = getUrlParams()
  const [page, setPage] = useState(1)
  const LIMIT = 15
  const [startDate, setStartDate] = useState(urlParams.defaultStartDate)
  const [endDate, setEndDate] = useState(urlParams.defaultEndDate)
  const [selectedConnectedStatus, setSelectedConnectedStatus] = useState(
    urlParams.defaultConnectedStatus
  )
  const [
    selectedVoiceAgentAnalysisResults,
    setSelectedVoiceAgentAnalysisResults,
  ] = useState(urlParams.defaultVoiceAgentAnalysisResults)
  const [selectedVoiceAgents, setSelectedVoiceAgents] = useState(
    urlParams.defaultVoiceAgents
  )

  const { data: voiceAgentsData, loading: voiceAgentsLoading } = useQuery(
    GET_VOICE_AGENTS,
    {
      variables: {
        pageInfo: {
          limit: 20,
          offset: 0,
        },
      },
    }
  )

  const voiceAgentOptions =
    voiceAgentsData?.voiceAgents?.items?.map((agent) => ({
      label: agent.name,
      value: agent.voiceAgentId,
    })) || []

  const { data: callMetricsData, loading: callMetricsLoading } = useQuery(
    GET_VOICE_AGENT_CALL_METRICS,
    {
      variables: {
        filters: {
          dateRange: { gte: startDate, lte: endDate },
          connectedStatuses: selectedConnectedStatus,
          voiceAgents: selectedVoiceAgents,
        },
      },
      onError: () => {
        toast('Unable to get call metrics, please try again.', 'error')
      },
    }
  )

  const { data: paginatedData, loading: paginatedLoading } = useQuery(
    GET_CALLS,
    {
      variables: {
        filters: {
          createdAt: { gte: startDate, lte: endDate },
          connectedStatuses: selectedConnectedStatus,
          voiceAgents: selectedVoiceAgents,
          analysisResults:
            selectedVoiceAgentAnalysisResults.length > 0
              ? selectedVoiceAgentAnalysisResults
              : Object.values(VoiceAgentAnalysisResultName),
        },
        pageInfo: {
          offset: (page - 1) * LIMIT,
          limit: LIMIT,
        },
      },
      onError: () => {
        toast('Unable to get calls, please try again.', 'error')
      },
    }
  )

  const totalConnectedCalls =
    callMetricsData?.getVoiceAgentCallMetrics?.totalConnectedCalls
  const totalCallsWithAnalysis =
    callMetricsData?.getVoiceAgentCallMetrics?.totalCallsWithAnalysis
  const paginatedResults = paginatedData?.getCalls?.items || []

  useEffect(() => {
    setPage(1)
  }, [
    selectedConnectedStatus,
    selectedVoiceAgentAnalysisResults,
    selectedVoiceAgents,
    startDate,
    endDate,
  ])

  return (
    <>
      <FiltersPortal>
        <div className="flex flex-row items-center gap-2">
          <FilterMenu
            filters={[
              {
                title: 'Analysis Results',
                selected: selectedVoiceAgentAnalysisResults,
                onChange: (value) => {
                  setSelectedVoiceAgentAnalysisResults(value)
                  updateUrl('analysisResults', value.join(','))
                },
                options: voiceAgentAnalysisResultOptions,
              },
              {
                title: `Voice Agent Status`,
                selected: selectedConnectedStatus,
                onChange: (value) => {
                  setSelectedConnectedStatus(value)
                  updateUrl('connectedStatus', value.join(','))
                },
                options: activityLogConnectedStatusOptions,
              },
              {
                title: `Voice Agent`,
                selected: selectedVoiceAgents,
                onChange: (value) => {
                  setSelectedVoiceAgents(value)
                  updateUrl('voiceAgents', value.join(','))
                },
                options: voiceAgentOptions,
              },
            ]}
          />
          <DateRangeMenu
            startDate={startDate}
            endDate={endDate}
            handleChange={(value) => {
              setStartDate(value[0])
              setEndDate(value[1])
              updateUrl(
                'dateRange',
                serializeDateRangeForUrl(value[0], value[1])
              )
            }}
            clearable={false}
          />
        </div>
      </FiltersPortal>

      <Grid gutter="xs" className="h-full overflow-y-auto overflow-x-hidden">
        <Grid.Col span={12}>
          <CallAnalysisAlert
            totalConnectedCalls={totalConnectedCalls}
            totalCallsWithAnalysis={totalCallsWithAnalysis}
            callMetricsLoading={callMetricsLoading}
          />
        </Grid.Col>
        <Grid.Col span={7}>
          <div className="flex flex-col gap-2">
            <AnalyticsCard
              title="Call Analysis Result Distribution"
              showBadge={false}
              description={`Distribution of call analysis by result category`}
            >
              <VoiceAgentBarChart
                totalConnectedCalls={totalConnectedCalls}
                filters={{
                  dateRange: { gte: startDate, lte: endDate },
                  connectedStatuses: selectedConnectedStatus,
                  analysisResults: selectedVoiceAgentAnalysisResults,
                  voiceAgents: selectedVoiceAgents,
                }}
              />
            </AnalyticsCard>
            <AnalyticsCard
              title="Call Analysis x Call Duration"
              showBadge={false}
              description={`Relationship between call analysis and call duration (max 20 results per category)`}
            >
              <VoiceAgentSwarmPlot
                filters={{
                  dateRange: { gte: startDate, lte: endDate },
                  connectedStatuses: selectedConnectedStatus,
                  analysisResults: selectedVoiceAgentAnalysisResults,
                  voiceAgents: selectedVoiceAgents,
                }}
              />
            </AnalyticsCard>
          </div>
        </Grid.Col>
        <Grid.Col span={5}>
          <AnalyticsCard
            title="Call Analysis Result List"
            showBadge={false}
            loading={paginatedLoading}
            description={'List of all call analysis results'}
            headerChildren={
              <div className="flex flex-col items-center justify-center">
                <div className="flex flex-row items-baseline gap-2 text-xl text-doubleNickel-gray-800">
                  {paginatedData?.getCalls?.totalCount}
                </div>
                <div className="flex flex-row items-center gap-2 text-xs font-medium text-doubleNickel-gray-600">
                  calls
                </div>
              </div>
            }
          >
            <VoiceAgentAnalysisTable
              voiceAgentAnalysisResults={paginatedResults}
            />
            <PaginationArea
              page={page}
              size="xs"
              totalPages={Math.ceil(
                paginatedData?.getCalls?.totalCount / LIMIT
              )}
              onPageChange={(page) => {
                setPage(page)
              }}
            />
          </AnalyticsCard>
        </Grid.Col>
      </Grid>
    </>
  )
}

export default CallQualityTab
